import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { UserPlus, Download } from "lucide-react";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "scientist", password: "Welcome@123" });

  const load = useCallback(async () => {
    try { const { data } = await api.get("/users"); setUsers(data); } catch (err) { console.error("[ARIA]", err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onInvite = async (e) => {
    e.preventDefault();
    try { await api.post("/users/invite", form); toast.success("User added"); setShow(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const onRole = async (id, role) => {
    await api.put(`/users/${id}/role`, { role }); toast.success("Role updated"); load();
  };
  const onDeactivate = async (id) => {
    if (!confirm("Deactivate this user?")) return;
    await api.delete(`/users/${id}`); toast.success("Deactivated"); load();
  };
  // close invite modal on Escape
  useEffect(() => {
    if (!show) return;
    const onKey = (e) => { if (e.key === "Escape") setShow(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show]);

  const onExport = async () => {
    const { data } = await api.get(`/users/export`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a"); a.href = url; a.download = "users.csv"; a.click();
  };

  return (
    <div className="h-screen overflow-y-auto p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-serif text-3xl mb-1">Team Members</h1>
            <p className="text-sm text-[var(--aria-text-muted)]">Manage who has access to your lab's ARIA.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={onExport} data-testid="users-export"
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-[var(--aria-border)] hover:bg-slate-50 dark:hover:bg-white/5">
              <Download size={14} /> Export CSV
            </button>
            <button onClick={() => setShow(true)} data-testid="users-invite-btn"
              className="flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-[#2563FF] text-white hover:bg-[#0B3BCE]">
              <UserPlus size={14} /> Invite user
            </button>
          </div>
        </div>

        <div className="aria-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#2563FF]/5 text-left text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Questions</th>
                <th className="px-4 py-3">Last active</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} className="border-t border-[var(--aria-border)]" data-testid={`user-row-${u.user_id}`}>
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3 text-[var(--aria-text-muted)]">{u.email}</td>
                  <td className="px-4 py-3">
                    <select value={u.role} onChange={e=>onRole(u.user_id, e.target.value)}
                      data-testid={`role-${u.user_id}`}
                      className="px-2 py-1 text-xs rounded border border-[var(--aria-border)] bg-[var(--aria-bg)]">
                      <option value="org_admin">org admin</option>
                      <option value="scientist">scientist</option>
                      <option value="viewer">viewer</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-[10px] rounded uppercase tracking-wider ${u.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                      {u.active ? "active" : "inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3">{u.question_count || 0}</td>
                  <td className="px-4 py-3 text-xs text-[var(--aria-text-muted)]">{u.last_active ? new Date(u.last_active).toLocaleString() : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => onDeactivate(u.user_id)} data-testid={`deactivate-${u.user_id}`}
                      className="text-xs text-red-600 hover:underline">Deactivate</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={()=>setShow(false)}>
          <form onSubmit={onInvite} onClick={e=>e.stopPropagation()} className="aria-card p-6 max-w-md w-full" data-testid="invite-modal">
            <h3 className="font-serif text-xl mb-4">Invite a user</h3>
            <div className="space-y-3">
              {[["name","Name","text"],["email","Email","email"],["password","Initial password","text"]].map(([k,l,t])=>(
                <div key={k}>
                  <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">{l}</label>
                  <input type={t} required value={form[k]} onChange={e=>setForm({...form,[k]:e.target.value})}
                    data-testid={`invite-${k}`}
                    className="w-full mt-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[#2563FF]" />
                </div>
              ))}
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">Role</label>
                <select value={form.role} onChange={e=>setForm({...form,role:e.target.value})} data-testid="invite-role"
                  className="w-full mt-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)]">
                  <option value="org_admin">Org Admin</option>
                  <option value="scientist">Scientist</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={()=>setShow(false)} className="px-4 py-2 text-sm rounded-md border border-[var(--aria-border)]">Cancel</button>
              <button type="submit" data-testid="invite-submit" className="px-4 py-2 text-sm rounded-md bg-[#2563FF] text-white hover:bg-[#0B3BCE]">Send invite</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
